import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { darken, LinearProgress, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Parser } from 'expr-eval';
import _ from 'lodash';
import React, { lazy, Suspense, useEffect, useState } from "react";
import Date from '../common/date.span';
import { useDialogs, useMenu } from '../common/dialog.util';
import StatusChip from '../common/status.chip';
import { activityApi } from "./activity.queries";
import ActivityScoreInput from './activity.score.input';

const ActivityRescheduleDialog = lazy(() => import('./activityReschedule.dialog'));
const ActivityUnscheduleDialog = lazy(() => import('./activityUnschedule.dialog'));
// const ActivityDialog = lazy(() => import('./activity.dialog'));

function ActivityRow(props) {
	const useUpdate = activityApi.useUpdate();
	const useDelete = activityApi.useDelete();
	let { activity, groups, depth } = props;
	const menu = useMenu();
	const dialogs = useDialogs();
	const theme = useTheme();

	const [expand, setExpand] = useState(activity?.status == 'In Progress');

	useEffect(() => {
		if (props.expandAll !== null) {
			setExpand(props.expandAll);
		}
	}, [props.expandAll]);

	if (!activity?.id || !activity?.courseDetail?.id) {
		return;
	}

	const toggleExpand = () => setExpand(!expand);

	const openEdit = () => {
		menu.close();
		dialogs.open('activity', activity);
	};
	const openAdd = () => {
		const newActivity = { reference: activity, courseId: props.course?.id, parentId: activity.parentId, type: activity.type, name: '', ordinal: activity.ordinal + 1 };
		menu.close();
		dialogs.open('activity', newActivity);
	};
	const openAddNextLevel = () => {
		const newActivity = { courseId: props.course?.id, parentId: activity.id, type: '', name: '', ordinal: 1 };
		menu.close();
		dialogs.open('activity', newActivity);
	};

	const onSelect = async (activity, file) => {
		activity.attachments = activity.attachments ? activity.attachments : [];
		activity.attachments.push(file);
		// setActivity({ ...activity });
	};

	const onUpload = async (activity, file) => {

	};

	const onDelete = async () => {
		await useDelete.mutateAsync(activity.id);
		menu.close();
	};

	const updateActivity = async (updated) => {
		if (updated.scoreRaw) {
			try {
				const expression = Parser.parse(updated.scoreRaw);
				const result = expression.evaluate();
				if (_.isNumber(result)) {
					updated.score = result < 1.9 ? result * 100 : result;
				}
			}
			catch (e) {
				updated.score = null;
			}
		}
		const result = await useUpdate.mutateAsync(updated);
	}
	const debouncedUpdateActivity = _.debounce(updateActivity, 500);


	const trSx = {
		position: 'relative',
		'th,td': {
			verticalAlign: 'top',
			'&:not(.MuiTableCell-paddingCheckbox)': {
				pt: 0.5,
			},
			backgroundColor: darken(theme.palette.background.paper, depth / 40),
		},
		'&:hover': {
			'> *': {
				backgroundColor: theme.palette.action.hover,
			},
			'.MuiOutlinedInput-notchedOutline': {
				borderWidth: 1,
			},
		},
		'&:focus-within': {
			'> *': {
				backgroundColor: theme.palette.action.selected,
			},
		},
		'.MuiOutlinedInput-notchedOutline': {
			borderWidth: 0,
		},
	};

	const size = 14;
	const fabSx = {
		position: 'absolute',
		left: 1 + (-size / 2),
		bottom: 1 + (-size / 2),
		minWidth: size,
		minHeight: size,
		width: size,
		height: size,
		svg: {
			height: size,
			width: size,
		}
	};

	const cellSx = { pl: depth * 2 + 1 };

	return (
		<React.Fragment>
			<TableRow className="hover-toggle" sx={[trSx, { '> *': { borderBottomWidth: expand ? 1 : 0 } }]}>
				<TableCell sx={cellSx}>
					{groups?.[activity.courseDetail.id]?.length > 0 &&
						<IconButton onClick={() => toggleExpand()}>
							{expand ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
						</IconButton>
					}
				</TableCell>
				<TableCell sx={cellSx}>
					<StatusChip status={activity.status} activity={activity} type="icon" />
				</TableCell>
				<TableCell component="th" scope="row" sx={cellSx}>
					{activity.courseDetail?.name}
				</TableCell>
				<TableCell sx={cellSx}>
					<Date date={activity.startDate} noTime={true} />
				</TableCell>
				<TableCell sx={cellSx}>
					<Date date={activity.endDate} noTime={true} />
				</TableCell>
				{/* <TableCell>
									<Date date={activity.dueDate} noTime={true} />
							</TableCell> */}
				<TableCell sx={cellSx}>
					<ActivityScoreInput activity={activity} />
				</TableCell>
				<TableCell sx={cellSx}>
					<Stack spacing={1} alignItems="center" justifyContent="flex-start">
						<LinearProgress variant="buffer" value={activity.completeness} valueBuffer={100} sx={{ width: 50, height: 12 }} color="info" />
						<Typography>{(activity.completeness?.toFixed(0) || 0) + '%'}</Typography>
					</Stack>
				</TableCell>
				<TableCell>
					<IconButton className="hover-muted" onClick={(e) => { menu.open(e); dialogs.setData([activity]); }}>
						<MoreVertIcon fontSize="inherit" />
					</IconButton>
					<Menu open={menu.isOpen} onClose={() => menu.close()} anchorEl={menu.anchor}>
						{dialogs.data?.courseDetail?.parentId == null && <MenuItem onClick={() => { menu.close(); dialogs.open('activityReschedule') }}>Reschedule...</MenuItem>}
						<MenuItem onClick={() => { menu.close(); dialogs.open('activityUnschedule') }}>Unschedule...</MenuItem>
						{/* <Divider />
				<MenuItem onClick={() => { menu.close(); onDelete(dialogs.data.id) }}>Cancel/Delete</MenuItem> */}
					</Menu>
					{dialogs.isOpen?.activityReschedule && (
						<Suspense fallback={null}>
							<ActivityRescheduleDialog activities={dialogs.data} open={dialogs.isOpen?.activityReschedule == true} onSubmit={dialogs.close} onCancel={dialogs.close} />
						</Suspense>
					)}
					{dialogs.isOpen?.activityUnschedule && (
						<Suspense fallback={null}>
							<ActivityUnscheduleDialog activities={dialogs.data} open={dialogs.isOpen?.activityUnschedule == true} onSubmit={dialogs.close} onCancel={dialogs.close} />
						</Suspense>
					)}
				</TableCell>
			</TableRow>
			{expand && groups?.[activity.courseDetail.id]?.length > 0 && _.chain(groups[activity.courseDetail.id]).sortBy('courseDetail.ordinal').map(a =>
				<ActivityRow activity={a} groups={groups} depth={depth + 1} key={a.id} expandAll={props.expandAll} />
			).value()}
		</React.Fragment>
	);
}

export default React.memo(ActivityRow);